import React from "react";
import { Field, ProBadge } from "./editorShared";
import { BookMarked, Bookmark, Layers, Plus, Star, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

export default function PackageGroupsSection({ addGroup, addPackage, isPro, openSaveGroup, proGate, q, removeGroup, removePackage, set, setDefaultPackage, setImportOpen, updateGroup, updatePackage }) {
  return (
    <section className="bg-card border border-slate-200/80 rounded-xl p-4 sm:p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3">
        <div className="flex items-center gap-2">
          <Layers style={{ width: 18, height: 18 }} className="text-primary" />
          <h2 className="text-base font-semibold text-slate-900">Gruppi di Pacchetti (Tier)</h2>
          {!isPro && <ProBadge testid="groups-pro-badge" onClick={() => proGate("I Gruppi di Pacchetti (Tier)")} />}
        </div>
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
          <Button size="sm" data-testid="quote-editor-import-catalog-group-button" onClick={() => setImportOpen(true)}>
            <BookMarked style={{ width: 15, height: 15 }} className="mr-1.5" /><span className="sm:hidden">Dal Listino</span><span className="hidden sm:inline">Importa dal Listino</span>
          </Button>
          <Button variant="outline" size="sm" data-testid="quote-editor-add-group-button" onClick={() => (isPro ? addGroup() : proGate("I Gruppi di Pacchetti (Tier)"))}>
            <Plus style={{ width: 15, height: 15 }} className="mr-1.5" /><span className="sm:hidden">Aggiungi gruppo</span><span className="hidden sm:inline">Aggiungi Gruppo Pacchetti</span>
          </Button>
        </div>
      </div>
      {q.groups.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Nessun gruppo. Crea un set di pacchetti mutuamente esclusivi tra cui il cliente sceglierà.</p>
      )}
      <div className="space-y-6">
        {q.groups.map((g, gi) => (
          <div key={g.id} data-testid={`editor-group-${gi}`} className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-4">
            <div className="flex flex-col-reverse sm:flex-row sm:items-start gap-2 sm:gap-3 mb-4">
              <div className="flex-1 w-full grid sm:grid-cols-2 gap-3">
                <Field label="Titolo del Gruppo">
                  <Input value={g.title} onChange={(e) => updateGroup(gi, { title: e.target.value })} placeholder="Es. Scegli il Piano di Sviluppo" />
                </Field>
                <Field label="Descrizione">
                  <Input value={g.description} onChange={(e) => updateGroup(gi, { description: e.target.value })} />
                </Field>
              </div>
              <div className="flex sm:flex-col gap-1 shrink-0 self-end sm:self-auto sm:mt-5">
                <Button variant="ghost" size="icon" onClick={() => openSaveGroup(g)} data-testid={`editor-save-group-catalog-${gi}`} className="text-slate-400 hover:text-primary" title="Salva nel Listino">
                  <Bookmark style={{ width: 16, height: 16 }} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => removeGroup(gi)} data-testid={`editor-remove-group-${gi}`} className="text-slate-400 hover:text-red-600">
                  <Trash2 style={{ width: 16, height: 16 }} />
                </Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.packages.map((p, pi) => (
                <div key={p.id} data-testid={`editor-group-${gi}-package-${pi}`} className={`rounded-lg border bg-card p-3 space-y-2.5 ${p.recommended ? "border-primary ring-1 ring-primary/30" : "border-border"}`}>
                  <div className="flex items-center gap-2">
                    <Input value={p.name} onChange={(e) => updatePackage(gi, pi, { name: e.target.value })} placeholder="Nome pacchetto" className="font-semibold h-9" />
                    {g.packages.length > 2 && (
                      <button onClick={() => removePackage(gi, pi)} data-testid={`editor-group-${gi}-remove-package-${pi}`} className="text-slate-300 hover:text-red-600 shrink-0">
                        <Trash2 style={{ width: 15, height: 15 }} />
                      </button>
                    )}
                  </div>
                  <Input value={p.subtitle} onChange={(e) => updatePackage(gi, pi, { subtitle: e.target.value })} placeholder="Sottotitolo" className="h-8 text-sm" />
                  <Textarea
                    rows={3}
                    value={Array.isArray(p.features) ? p.features.join("\n") : p.features || ""}
                    onChange={(e) => updatePackage(gi, pi, { features: e.target.value.split("\n") })}
                    placeholder="Punti chiave (uno per riga)"
                    className="text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Prezzo">
                      <Input type="number" min="0" step="0.01" value={p.price} onFocus={(e) => e.target.select()} onChange={(e) => updatePackage(gi, pi, { price: e.target.value })} className="font-mono h-8" />
                    </Field>
                    <Field label="Giorni">
                      <Input type="number" min="0" value={p.work_days} onFocus={(e) => e.target.select()} onChange={(e) => updatePackage(gi, pi, { work_days: e.target.value })} className="font-mono h-8" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Sconto">
                      <Select value={p.discount_type || "none"} onValueChange={(v) => updatePackage(gi, pi, { discount_type: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nessuno</SelectItem>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="amount">€</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label={p.discount_type === "percent" ? "Val. %" : "Val. €"}>
                      <Input type="number" min="0" step="0.01" value={p.discount_value} disabled={!p.discount_type || p.discount_type === "none"} onFocus={(e) => e.target.select()} onChange={(e) => updatePackage(gi, pi, { discount_value: e.target.value })} className="font-mono h-8 disabled:opacity-50" />
                    </Field>
                  </div>
                  <label className="flex items-center justify-between text-sm text-slate-600">
                    <span className="inline-flex items-center gap-1"><Star style={{ width: 13, height: 13 }} /> Consigliato</span>
                    <Switch checked={!!p.recommended} onCheckedChange={(v) => updatePackage(gi, pi, { recommended: v })} data-testid={`editor-group-${gi}-package-${pi}-recommended`} />
                  </label>
                  <label className="flex items-center justify-between text-sm text-slate-600">
                    <span>Selezionato di default</span>
                    <Switch checked={!!p.default_selected} onCheckedChange={(v) => { if (v) setDefaultPackage(gi, pi); }} data-testid={`editor-group-${gi}-package-${pi}-default`} />
                  </label>
                </div>
              ))}
            </div>
            {g.packages.length < 4 && (
              <Button variant="outline" size="sm" onClick={() => addPackage(gi)} data-testid={`editor-add-package-${gi}`} className="mt-3 w-full sm:w-auto">
                <Plus style={{ width: 14, height: 14 }} className="mr-1.5" /> Aggiungi opzione
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
